/**
 * patients.service.ts
 *
 * Typed API call wrappers for the patients domain.
 * All methods use the shared apiClient (Axios instance with JWT interceptor).
 * Returns unwrapped data from the response envelope — callers never see Axios wrappers.
 */
import { apiClient } from './apiClient';
import { SIZE_THRESHOLD, presignAndPutFile, uploadFile } from './uploads.service';
import type { PaginatedResponse, ApiResponse } from '@/types/api.types';
import type {
  Patient,
  PatientSummary,
  PatientListParams,
} from '@/types/patient.types';

// ─── Domain-specific types (aligned with server patients.types.ts) ────────────

export interface PatientNote {
  noteId: number;
  patientId: number;
  planId: number | null;
  message: string;
  userId: number;
  userName: string;
  createdAt: string;
  isQa: boolean;
}

export interface PatientPhoto {
  photoId: number;
  patientId: number;
  fileName: string;
  filePath: string;
  isDefault: boolean;
  uploadedAt: string;
}

export interface CreatePatientPayload {
  firstName: string;
  lastName: string;
  middleName?: string | null;
  informalName?: string | null;
  email?: string | null;
  birthDate?: string | null;
  gender?: string | null;
  address?: string | null;
  address2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  homePhone?: string | null;
  cellPhone?: string | null;
  workPhone?: string | null;
  referralGeneral?: string | null;
  referralSpecific?: string | null;
  referralFirstname?: string | null;
  referralLastname?: string | null;
  referralOther?: string | null;
  emailOptIn?: boolean;
  smsOptIn?: boolean;
  preferredSmsNumber?: string | null;
}

export type UpdatePatientPayload = Partial<CreatePatientPayload> & {
  maidenName?: string | null;
  fax?: string | null;
  homeExt?: string | null;
  workExt?: string | null;
  contactId?: number | null;
  touchmdId?: string | null;
  facilityID?: number | null;
  /**
   * Legacy: the current forms send only the `*CountryCode` dial codes and the
   * API derives the ID from the code. Kept for PatientInfoDemographicsTabLegacy.
   */
  phoneCountryID?: string | null;
  /** The home phone's dial code, e.g. '44'. */
  phoneCountryCode?: string | null;
  /** Legacy — see phoneCountryID. */
  cellCountryID?: string | null;
  /** The cell's dial code, e.g. '44'. */
  cellCountryCode?: string | null;
  emailUnsubscribe?: boolean;
  smsUnsubscribe?: boolean;
  displayID?: string | null;
  countryID?: number | null;
  stateID?: number | null;
};

export interface PatientRecord extends Patient {
  informalName?: string | null;
  maidenName?: string | null;
  facilityID?: number | null;
  facilityName?: string | null;
  phoneCountryID?: string | null;
  phoneCountryCode?: string | null;
  cellCountryID?: string | null;
  cellCountryCode?: string | null;
  emailVerified?: boolean;
  emailVerifyDate?: string | null;
  phoneVerified?: boolean;
  phoneVerifyDate?: string | null;
  credentialsPhoneNumber?: string | null;
  emailUnsubscribe?: boolean;
  smsUnsubscribe?: boolean;
  emailInvalid?: boolean;
  emailInvalidReason?: string | null;
  phoneInvalid?: boolean;
  phoneInvalidReason?: string | null;
  passwordExists?: boolean;
  /** Patient has a Cognito identity (signed up / signs in via SSO). */
  ssoLinked?: boolean;
  portalStatus?: 'Registered' | 'Invited' | 'Not invited' | string | null;
  portalStatusDate?: string | null;
  countryID?: number | null;
  countryName?: string | null;
  countryAbbr?: string | null;
  phoneCode?: string | null;
  image?: string | null;
  thumb?: string | null;
  /** Raw v4 transactional opt-in flags — passed straight through, not normalized. */
  emailOptIn?: boolean | number | string | null;
  smsOptIn?: boolean | number | string | null;
  /**
   * Flat v4 `contacts` columns, exactly as PatientsController::show sends them.
   * `Patient` models the same person with a structured `address: PatientAddress`
   * and a `sex` enum, neither of which this endpoint returns; reconciling those
   * two shapes is a separate change, so the flat columns are declared here and
   * `address` (the one name that collides) is read through a narrow cast at the
   * single call site that needs it.
   */
  gender?: string | null;
  birthDate?: string | null;
  address2?: string | null;
  zip?: string | null;
}

/**
 * Transactional opt-in flags arrive raw from v4 (`'1'`/`'0'` or 1/0) and default
 * to *allowed* when absent — the same polarity as the API's send guards
 * (`CommunicationsSendController::sendSms`/`sendEmail` both use `?? '1'`), so a
 * patient is only treated as opted out on an explicit `0`.
 */
export function isEmailOptedOut(patient: Pick<PatientRecord, 'emailOptIn'> | null | undefined): boolean {
  return String(patient?.emailOptIn ?? '1') === '0';
}

export function isSmsOptedOut(patient: Pick<PatientRecord, 'smsOptIn'> | null | undefined): boolean {
  return String(patient?.smsOptIn ?? '1') === '0';
}

export interface PatientFacilityOption {
  facilityId: number;
  facilityName: string;
  active: boolean;
}

export interface CountryCodeOption {
  countryID: number;
  countryName: string;
  countryAbbr: string | null;
  phoneCode: string | null;
  phonePattern: string | null;
}

export interface AddNotePayload {
  message: string;
  planId?: number | null;
  isQa?: boolean;
}

function numberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function numberOrZero(value: unknown): number {
  return numberOrNull(value) ?? 0;
}

function stringOrNull(value: unknown): string | null {
  if (value === null || value === undefined) {
    return null;
  }

  const text = String(value).trim();
  return text === '' ? null : text;
}

function booleanOrFalse(value: unknown): boolean {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'number') {
    return value !== 0;
  }

  const text = stringOrNull(value)?.toLowerCase();
  return text === '1' || text === 'true' || text === 'yes';
}

/** `hasPhoto` as the API sends it; undefined when the payload predates the flag. */
function hasPhotoFlag(value: unknown): boolean | undefined {
  if (value === undefined || value === null) {
    return undefined;
  }
  return booleanOrFalse(value);
}

/**
 * Resolve the avatar URL for the tri-state `usePatientPhoto` contract:
 *   - the API said `hasPhoto: false`  → `null` (known none, no request)
 *   - the API sent a `photoUrl` string → that URL
 *   - otherwise (legacy shape, no flag) → the by-id route, which the hook
 *     fetches and which now answers 204 (not 404) when there is no photo.
 */
export function buildPatientPhotoUrl(patientId: number, photoUrl: unknown, hasPhoto?: unknown): string | null {
  if (hasPhotoFlag(hasPhoto) === false) {
    return null;
  }

  const direct = stringOrNull(photoUrl);
  if (direct) {
    return direct;
  }

  if (patientId <= 0) {
    return null;
  }

  return `/patients/${patientId}/photo`;
}

function normalizePatientSummary(
  raw: Record<string, unknown>,
  statusFallback: PatientSummary['status'] = 'active',
): PatientSummary {
  const patientId = numberOrZero(raw.patientId ?? raw.patientID);
  const firstName = String(raw.firstName ?? '').trim();
  const lastName = String(raw.lastName ?? '').trim();
  const displayName = [firstName, lastName].filter(Boolean).join(' ').trim();
  const totalSpent = numberOrNull(raw.totalSpent ?? raw.totalAmount) ?? 0;
  const planCount = numberOrNull(raw.planCount ?? raw.totalPlans) ?? 0;

  return {
    patientId,
    accountId: numberOrZero(raw.accountId ?? raw.accountID),
    firstName,
    lastName,
    displayName: displayName || `Patient #${patientId}`,
    displayId: stringOrNull(raw.displayId ?? raw.displayID),
    dateOfBirth: stringOrNull(raw.dateOfBirth ?? raw.birthDate),
    phone: stringOrNull(raw.phone),
    homePhone: stringOrNull(raw.homePhone),
    cellPhone: stringOrNull(raw.cellPhone),
    email: stringOrNull(raw.email),
    photoUrl: buildPatientPhotoUrl(patientId, raw.photoUrl, raw.hasPhoto),
    hasPhoto: hasPhotoFlag(raw.hasPhoto),
    status: (raw.status as PatientSummary['status'] | undefined) ?? statusFallback,
    activePlanId: numberOrNull(raw.activePlanId ?? raw.activePlanID),
    activePlanStage: stringOrNull(raw.activePlanStage ?? raw.stageName),
    city: stringOrNull(raw.city),
    state: stringOrNull(raw.state),
    tags: Array.isArray(raw.tags) ? (raw.tags as PatientSummary['tags']) : [],
    referralSourceName: stringOrNull(raw.referralSourceName ?? raw.referralSource),
    coordinatorName: stringOrNull(raw.coordinatorName),
    facilityName: stringOrNull(raw.facilityName),
    totalSpent,
    balanceDue: numberOrNull(raw.balanceDue) ?? 0,
    planCount,
    createdAt: stringOrNull(raw.createdAt ?? raw.dateAdded),
  };
}

function normalizePatient(patient: Record<string, unknown>): PatientRecord {
  const patientId = numberOrZero(patient.patientId ?? patient.patientID);
  const firstName = String(patient.firstName ?? '').trim();
  const lastName = String(patient.lastName ?? '').trim();
  const addon = (patient.addon ?? {}) as Record<string, unknown>;

  return {
    ...(patient as unknown as PatientRecord),
    patientId,
    accountId: numberOrZero(patient.accountId ?? patient.accountID),
    firstName,
    lastName,
    displayName:
      `${firstName} ${lastName}`.trim() || `Patient #${patientId}`,
    displayId: stringOrNull(patient.displayId ?? patient.displayID),
    photoUrl: buildPatientPhotoUrl(patientId, patient.photoUrl, patient.hasPhoto),
    hasPhoto: hasPhotoFlag(patient.hasPhoto),
    facilityID: numberOrNull(patient.facilityID ?? patient.facilityId),
    facilityName: stringOrNull(patient.facilityName),
    phoneCountryID: stringOrNull(patient.phoneCountryID),
    phoneCountryCode: stringOrNull(patient.phoneCountryCode),
    cellCountryID: stringOrNull(patient.cellCountryID),
    cellCountryCode: stringOrNull(patient.cellCountryCode),
    emailVerified: booleanOrFalse(patient.emailVerified),
    emailVerifyDate: stringOrNull(patient.emailVerifyDate),
    phoneVerified: booleanOrFalse(patient.phoneVerified),
    phoneVerifyDate: stringOrNull(patient.phoneVerifyDate),
    credentialsPhoneNumber: stringOrNull(patient.credentialsPhoneNumber),
    emailUnsubscribe: booleanOrFalse(patient.emailUnsubscribe ?? addon.emailUnsubscribe),
    smsUnsubscribe: booleanOrFalse(patient.smsUnsubscribe ?? addon.smsUnsubscribe),
    emailInvalid: booleanOrFalse(patient.emailInvalid),
    emailInvalidReason: stringOrNull(patient.emailInvalidReason),
    phoneInvalid: booleanOrFalse(patient.phoneInvalid),
    phoneInvalidReason: stringOrNull(patient.phoneInvalidReason),
    passwordExists: booleanOrFalse(patient.passwordExists),
    ssoLinked: booleanOrFalse(patient.ssoLinked),
    portalStatus: stringOrNull(patient.portalStatus) ?? 'Not invited',
    portalStatusDate: stringOrNull(patient.portalStatusDate),
    countryID: numberOrNull(patient.countryID),
    countryName: stringOrNull(patient.countryName),
    countryAbbr: stringOrNull(patient.countryAbbr),
    phoneCode: stringOrNull(patient.phoneCode),
    informalName: stringOrNull(patient.informalName),
    maidenName: stringOrNull(patient.maidenName),
    tags: Array.isArray(patient.tags) ? (patient.tags as PatientRecord['tags']) : [],
  };
}

// ─── Service methods ──────────────────────────────────────────────────────────

export interface UnifiedPatientListParams {
  filtersArray?: string;
  search?: string;
  status?: string;
  page?: number;
  perPage?: number;
  limit?: number;
  sort?: string;
  order?: 'asc' | 'desc';
}

/**
 * Fetch a paginated list of patients, optionally filtered.
 * Accepts either the unified filtersArray format or legacy per-param format.
 */
export function getPatients(
  filters: PatientListParams | UnifiedPatientListParams,
  pagination: { page?: number; perPage?: number; sort?: string; order?: 'asc' | 'desc' } = {},
): Promise<PaginatedResponse<PatientSummary>> {
  const { limit, sortBy, sortDir, ...rest } = filters as PatientListParams;
  const params: Record<string, unknown> = { ...rest, ...pagination };
  if (limit != null && params.perPage == null) params.perPage = limit;
  if (sortBy && !params.sort) params.sort = sortBy;
  if (sortDir && !params.order) params.order = sortDir;

  const statusFallback: PatientSummary['status'] = (filters as PatientListParams).status === 'deleted' ? 'deleted' : (filters as PatientListParams).status || 'active';

  return apiClient
    .get<PaginatedResponse<Record<string, unknown>>>('/patients', { params })
    .then((r) => ({
      ...r.data,
      data: (r.data.data ?? []).map((row) => normalizePatientSummary(row, statusFallback)),
    }));
}

/**
 * KPI counts for the patients list page.
 */
export interface PatientKpis {
  total: number;
  active: number;
  new: number;
  cold: number;
}

export function getPatientKpis(): Promise<PatientKpis> {
  return apiClient.get<{ data: PatientKpis }>('/patients/kpis').then((r) => r.data.data);
}

export interface PatientSidebarStats {
  totalSpend: number;
  clientSince: string | null;
  appointmentCount: number;
  noShowCount: number;
  compedTotal?: number;
  compedValue?: number;
  lastAppointmentDate?: string | null;
  lastAppointmentType?: string | null;
}

export function getPatientSidebarStats(patientId: number): Promise<PatientSidebarStats> {
  return apiClient
    .get<{ data: PatientSidebarStats }>(`/patients/${patientId}/sidebar-stats`)
    .then((r) => r.data.data);
}

/**
 * Autocomplete / typeahead search — returns lightweight patient summaries.
 */
export function searchPatients(q: string): Promise<PatientSummary[]> {
  return apiClient
    .get<{ data: Record<string, unknown>[] }>('/patients/search', { params: { q } })
    .then((r) =>
      (r.data.data ?? []).map((raw) => {
        const firstName = String(raw.firstName ?? '');
        const lastName  = String(raw.lastName  ?? '');
        const patientId = Number(raw.patientId ?? raw.patientID ?? 0);
        return {
          patientId,
          accountId:        Number(raw.accountId ?? raw.accountID ?? 0),
          firstName,
          lastName,
          displayName:      [firstName, lastName].filter(Boolean).join(' '),
          displayId:        raw.displayId != null ? String(raw.displayId) : null,
          dateOfBirth:      (raw.dateOfBirth ?? raw.birthDate ?? null) as string | null,
          phone:            (raw.phone ?? raw.cellPhone ?? null) as string | null,
          homePhone:        (raw.homePhone ?? null) as string | null,
          cellPhone:        (raw.cellPhone ?? null) as string | null,
          email:            (raw.email ?? null) as string | null,
          // The typeahead payload carries no availability, so this resolves to
          // the by-id route ("unknown"), never to null ("known none").
          photoUrl:         buildPatientPhotoUrl(patientId, raw.photoUrl, raw.hasPhoto),
          hasPhoto:         hasPhotoFlag(raw.hasPhoto),
          status:           (raw.status ?? 'active') as PatientSummary['status'],
          activePlanId:     raw.activePlanId != null ? Number(raw.activePlanId) : null,
          activePlanStage:  (raw.activePlanStage ?? null) as string | null,
        } satisfies PatientSummary;
      }),
    );
}

/**
 * Fetch the full patient record by ID.
 */
export function getPatient(id: number): Promise<PatientRecord> {
  return apiClient
    .get<ApiResponse<Record<string, unknown>>>(`/patients/${id}`)
    .then((r) => normalizePatient(r.data.data));
}

/**
 * Create a new patient record.
 */
export function createPatient(data: CreatePatientPayload): Promise<PatientRecord> {
  return apiClient
    .post<ApiResponse<Record<string, unknown>>>('/patients', data)
    .then((r) => normalizePatient(r.data.data));
}

/**
 * Update an existing patient record (partial update supported).
 */
export function updatePatient(id: number, data: UpdatePatientPayload): Promise<PatientRecord> {
  return apiClient
    .put<ApiResponse<Record<string, unknown>>>(`/patients/${id}`, data)
    .then((r) => normalizePatient(r.data.data));
}

/**
 * Soft-delete a patient. Requires admin access (enforced server-side).
 */
export function deletePatient(id: number): Promise<void> {
  return apiClient.delete(`/patients/${id}`).then(() => undefined);
}

/**
 * Attach a tag to a patient.
 */
export function addTag(patientId: number, tagId: number): Promise<void> {
  return apiClient
    .post(`/patients/${patientId}/tags`, { tagId })
    .then(() => undefined);
}

/**
 * Remove a tag from a patient.
 */
export function removeTag(patientId: number, tagId: number): Promise<void> {
  return apiClient
    .delete(`/patients/${patientId}/tags/${tagId}`)
    .then(() => undefined);
}

/**
 * Upload a patient photo. Sends as multipart/form-data.
 */
export function uploadPhoto(patientId: number, file: File): Promise<PatientPhoto> {
  // uploadFile switches photos > 4 MB to the presigned-S3 flow (Lambda 6 MB cap).
  return uploadFile(`/patients/${patientId}/photo`, file, undefined, { fileFieldName: 'photo' }).then(
    (r) => r.data.data,
  );
}

/**
 * Set a photo as the patient's default profile image.
 */
export function setDefaultPhoto(patientId: number, photoId: number): Promise<void> {
  return apiClient
    .put(`/patients/${patientId}/photo/${photoId}/default`)
    .then(() => undefined);
}

/**
 * Delete a patient photo.
 */
export function deletePhoto(patientId: number, photoId: number): Promise<void> {
  return apiClient
    .delete(`/patients/${patientId}/photo/${photoId}`)
    .then(() => undefined);
}

/**
 * Merge two patient records. Mirrors the legacy
 * patient_merge_processor.php contract verbatim: { patientID, merge, keep }
 * where `keep === 'patient'` keeps `patientID` and `keep === 'merge'` keeps
 * `merge`. The non-kept record has every child row reassigned to the kept
 * record then is soft-deleted. Returns the IDs of both sides.
 */
export interface MergePatientsRequest {
  patientID: number;
  merge: number;
  keep: 'patient' | 'merge';
}

export interface MergePatientsResult {
  keptPatientId: number;
  mergedPatientId: number;
}

export function mergePatients(params: MergePatientsRequest): Promise<MergePatientsResult> {
  return apiClient
    .post<ApiResponse<MergePatientsResult>>('/patients/merge', params)
    .then((r) => r.data.data);
}

/**
 * Fetch all notes for a patient.
 */
export function getPatientNotes(patientId: number): Promise<PatientNote[]> {
  return apiClient
    .get<ApiResponse<PatientNote[]>>(`/patients/${patientId}/notes`)
    .then((r) => r.data.data);
}

/**
 * Add a note to a patient record.
 */
export function addPatientNote(patientId: number, note: AddNotePayload): Promise<PatientNote> {
  return apiClient
    .post<ApiResponse<PatientNote>>(`/patients/${patientId}/notes`, note)
    .then((r) => r.data.data);
}

// ─── Health History / Medical Clearance ───────────────────────────────────────

export interface MedicalClearanceOption {
  id: number;
  label: string;
}

export interface MedicalClearanceHistoryEntry {
  value: string;
  displayValue: string;
  createdAt: string | null;
  userId: string | number | null;
  userName: string | null;
}

export interface MedicalClearanceField {
  fieldId: string;
  label: string;
  type: 'text' | 'textarea' | 'dropdown' | 'checkbox' | 'radio';
  kind: 'health' | 'record';
  questionKey: string | null;
  recordFieldId: number | null;
  value: string;
  options: MedicalClearanceOption[];
  rows: number | null;
  group: string | null;
  history: MedicalClearanceHistoryEntry[];
}

export interface VitalsHistoryRow {
  date: string;
  heightFeet: string | null;
  heightInches: string | null;
  weight: string | null;
  bpSystolic: string | null;
  bpDiastolic: string | null;
  heartRate: string | null;
  temperature: string | null;
}

export interface MedicalClearanceSection {
  sectionId: number;
  title: string;
  type: 'vital_statistics' | 'medical_clearance_upload' | 'custom';
  fields: MedicalClearanceField[];
  lastUpdatedAt: string | null;
  lastUpdatedBy: string | null;
  readOnly: boolean;
  age?: number | null;
  vitalsHistory?: VitalsHistoryRow[];
}

export interface MedicalClearanceFile {
  name: string;
  size: number;
  extension: string;
  modifiedAt: string;
}

export interface HealthHistory {
  patientId: number;
  accountId: number;
  formType: string;
  age: number | null;
  lastUpdatedAt: string | null;
  lastUpdatedBy: string | null;
  sections: MedicalClearanceSection[];
  files: MedicalClearanceFile[];
}

export interface UpsertHealthHistoryPayload {
  healthFields: Record<string, string>;
  recordFields: Array<{
    recordFieldId: number;
    value: string;
  }>;
  isPatient?: boolean;
}

// ─── Patient Demographics ─────────────────────────────────────────────────────

export interface DemographicsHistoryEntry {
  value: string;
  displayValue: string;
  createdAt: string | null;
  userId: string | number | null;
  userName: string | null;
  /** The write path that produced this entry ('staff-update', 'sms-stop', ...). */
  source?: string;
  /**
   * The change reached this account through the shared master record rather
   * than being made here. Never says which account it came from.
   */
  viaMaster?: boolean;
}

export interface PatientDemographics {
  nickname: string | null;
  chartId: string | null;
  languageId: number | null;
  interpreterNeeded: boolean | null;
  rawFields: Record<string, string | null>;
  fieldHistory: Record<string, DemographicsHistoryEntry[]>;
}

export interface UpsertDemographicsPayload {
  nickname?: string | null;
  chartId?: string | null;
  languageId?: number | null;
  interpreterNeeded?: boolean | null;
  rawFields?: Record<string, string | null>;
}

const DEMOGRAPHICS_FIELD_MAP = {
  nickname: 'nickname',
  chartId: 'chart_id',
  languageId: 'language',
  interpreterNeeded: 'interpreterNeeded',
} as const;

function normalizeDemographics(raw: Record<string, unknown> | null): PatientDemographics | null {
  if (!raw || Array.isArray(raw) || Object.keys(raw).length === 0) {
    return null;
  }

  const fields = (raw.fields ?? raw) as Record<string, unknown>;
  const historyRaw = (raw.history ?? {}) as Record<string, DemographicsHistoryEntry[]>;

  const rawFields = Object.fromEntries(
    Object.entries(fields).map(([key, value]) => [key, stringOrNull(value)])
  );

  const languageId = numberOrNull(fields[DEMOGRAPHICS_FIELD_MAP.languageId]);
  const interpreterRaw = fields[DEMOGRAPHICS_FIELD_MAP.interpreterNeeded];
  const interpreterNeeded =
    interpreterRaw === null || interpreterRaw === undefined || String(interpreterRaw).trim() === ''
      ? null
      : booleanOrFalse(interpreterRaw);

  return {
    nickname: stringOrNull(fields[DEMOGRAPHICS_FIELD_MAP.nickname]),
    chartId: stringOrNull(fields[DEMOGRAPHICS_FIELD_MAP.chartId]),
    languageId,
    interpreterNeeded,
    rawFields,
    fieldHistory: historyRaw,
  };
}

function serializeDemographicsPayload(data: UpsertDemographicsPayload): Record<string, string> {
  const fields: Record<string, string> = {};
  const merged = { ...(data.rawFields ?? {}) };

  if ('nickname' in data) {
    merged[DEMOGRAPHICS_FIELD_MAP.nickname] = data.nickname ?? null;
  }

  if ('chartId' in data) {
    merged[DEMOGRAPHICS_FIELD_MAP.chartId] = data.chartId ?? null;
  }

  if ('languageId' in data) {
    merged[DEMOGRAPHICS_FIELD_MAP.languageId] =
      data.languageId == null ? null : String(data.languageId);
  }

  if ('interpreterNeeded' in data) {
    merged[DEMOGRAPHICS_FIELD_MAP.interpreterNeeded] =
      data.interpreterNeeded == null ? null : data.interpreterNeeded ? '1' : '0';
  }

  for (const [key, value] of Object.entries(merged)) {
    fields[key] = value ?? '';
  }

  return fields;
}

/**
 * Fetch the health history record for a patient. Returns null if none exists yet.
 */
export function getHealthHistory(patientId: number): Promise<HealthHistory | null> {
  return apiClient
    .get<ApiResponse<HealthHistory | null>>(`/patients/${patientId}/health-history`)
    .then((r) => r.data.data);
}

/**
 * Create or update the health history record for a patient.
 */
export function upsertHealthHistory(
  patientId: number,
  data: UpsertHealthHistoryPayload,
): Promise<HealthHistory> {
  return apiClient
    .put<ApiResponse<HealthHistory>>(`/patients/${patientId}/health-history`, data)
    .then((r) => r.data.data);
}

export function listHealthHistoryFiles(patientId: number): Promise<MedicalClearanceFile[]> {
  return apiClient
    .get<ApiResponse<MedicalClearanceFile[]>>(`/patients/${patientId}/health-history/files`)
    .then((r) => r.data.data);
}

export async function uploadHealthHistoryFile(patientId: number, file: File): Promise<MedicalClearanceFile> {
  if (file.size > SIZE_THRESHOLD) {
    // Shared helper rather than an inline presign + PUT: the payload arrives
    // wrapped by apiClient's response interceptor, and reading it flat yields an
    // undefined URL that uploads nothing and then posts "undefined" as the key.
    const s3Key = await presignAndPutFile(file);

    const form = new FormData();
    form.append('s3Key', s3Key);
    return apiClient
      .post<ApiResponse<MedicalClearanceFile>>(`/patients/${patientId}/health-history/files`, form)
      .then((r) => r.data.data);
  }

  const formData = new FormData();
  formData.append('file', file);

  return apiClient
    .post<ApiResponse<MedicalClearanceFile>>(`/patients/${patientId}/health-history/files`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    .then((r) => r.data.data);
}

export function downloadHealthHistoryFile(patientId: number, fileName: string): Promise<string> {
  return apiClient
    .get<Blob>(`/patients/${patientId}/health-history/files/download`, {
      params: { name: fileName },
      responseType: 'blob',
    })
    .then((r) => URL.createObjectURL(r.data));
}

export function deleteHealthHistoryFiles(patientId: number, files: string[]): Promise<string[]> {
  return apiClient
    .delete<ApiResponse<{ deleted: string[] }>>(`/patients/${patientId}/health-history/files`, {
      data: { files },
    })
    .then((r) => r.data.data.deleted ?? []);
}

/**
 * Fetch the extended demographics record for a patient. Returns null if none exists yet.
 */
export function getDemographics(patientId: number): Promise<PatientDemographics | null> {
  return apiClient
    .get<ApiResponse<Record<string, unknown> | null>>(`/patients/${patientId}/demographics`)
    .then((r) => normalizeDemographics(r.data.data));
}

/**
 * Create or update the extended demographics record for a patient.
 */
export function upsertDemographics(
  patientId: number,
  data: UpsertDemographicsPayload,
): Promise<PatientDemographics> {
  return apiClient
    .put<ApiResponse<Record<string, unknown>>>(`/patients/${patientId}/demographics`, {
      fields: serializeDemographicsPayload(data),
    })
    .then((r) => normalizeDemographics(r.data.data) ?? {
      nickname: null,
      chartId: null,
      languageId: null,
      interpreterNeeded: null,
      rawFields: {},
      fieldHistory: {},
    });
}

export function listPatientFacilities(): Promise<PatientFacilityOption[]> {
  return apiClient
    .get<ApiResponse<Record<string, unknown>[]>>('/facilities')
    .then((r) =>
      (r.data.data ?? []).map((row) => ({
        facilityId: numberOrZero(row.facilityId ?? row.facilityID),
        facilityName: String(row.facilityName ?? row.name ?? '').trim() || 'Unnamed facility',
        active: booleanOrFalse(row.active ?? true),
      })),
    );
}

export function listCountryCodes(): Promise<CountryCodeOption[]> {
  return apiClient
    .get<ApiResponse<Record<string, unknown>[]>>('/country-codes')
    .then((r) =>
      (r.data.data ?? []).map((row) => ({
        countryID: numberOrZero(row.countryID),
        countryName: String(row.countryName ?? '').trim(),
        countryAbbr: stringOrNull(row.countryAbbr),
        phoneCode: stringOrNull(row.phoneCode),
        phonePattern: stringOrNull(row.phonePattern),
      })),
    );
}

export function getAccountCountryAbbr(): Promise<string> {
  return apiClient
    .get<ApiResponse<Record<string, unknown>[]>>('/accounts-country')
    .then((r) => {
      const list = r.data.data ?? [];
      if (list.length > 0 && list[0].country && typeof list[0].country === 'object') {
        const country = list[0].country as Record<string, unknown>;
        const abbr = String(country.countryAbbr ?? '').trim();
        if (abbr) return abbr;
      }
      return 'US';
    })
    .catch(() => 'US');
}

/**
 * A shared patient — one identity held across accounts — that already owns the
 * email or phone being saved.
 *
 * Named `master*` because that is what the API calls it; NOTHING built from this
 * may say "master" to the user. The surface wording is "a shared patient from
 * another account".
 *
 * Carries that record's own name, email and phone so staff can confirm they are
 * looking at the right person — one of the two contact values was typed by them
 * to produce the match. It never says which accounts that person belongs to.
 */
export interface MasterConflictInfo {
  masterPatientId: number;
  /** Linking is only offered when this patient is unlinked and the shared
   *  record has no other patient in this account. */
  canLink: boolean;
  matchedOn: string[];
  email: string;
  cellPhone: string;
  /** Absent on an older API build, so treat both as optional. */
  firstName?: string;
  lastName?: string;
}

export interface CheckCredentialsResult {
  valid: boolean;
  errors: Record<string, string>;
  master?: MasterConflictInfo | null;
}

export interface LinkPatientToMasterPayload {
  masterPatientId: number;
  email?: string;
  cellPhone?: string;
}

/**
 * Link an existing patient to a shared record. Two steps by design: this only
 * creates the link, and the caller then re-sends its save, which now cascades
 * with the patient linked.
 */
export function linkPatientToMaster(
  patientId: number,
  payload: LinkPatientToMasterPayload,
): Promise<{ status: string; masterPatientId: number }> {
  return apiClient
    .post<ApiResponse<{ status: string; masterPatientId: number }>>(
      `/patients/${patientId}/master-link`,
      payload,
    )
    .then((r) => r.data.data);
}

/** `cellCountryCode` is the cell's dial code ('44'), never a countryID. */
export function checkCredentials(
  patientId: number | null,
  email: string,
  cellPhone: string,
  cellCountryCode: string,
): Promise<CheckCredentialsResult> {
  return apiClient
    .post<ApiResponse<CheckCredentialsResult>>('/patients/check-credentials', {
      patientId,
      email,
      cellPhone,
      cellCountryCode,
    })
    .then((r) => r.data.data);
}

export interface SendAppInviteResult {
  sent: boolean;
  portalStatus: string;
  portalStatusDate: string | null;
}

/**
 * Send (or re-send) the patient-portal / app registration invite email.
 * Backend flips App Status to "Invited" (or leaves "Registered" and sends a
 * login link) and returns the refreshed status.
 */
export function sendAppInvite(patientId: number): Promise<SendAppInviteResult> {
  return apiClient
    .post<ApiResponse<SendAppInviteResult>>(`/patients/${patientId}/app-invite`, {})
    .then((r) => r.data.data);
}
